import React, { FC, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import {
  Box,
  ButtonIcon,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import theme from '@cubeyou-team/hx-theme';

import { usePanelHeightResize } from '../../../../modules/query-builder/hooks/use-panel-resize/usePanelHeightResize';
import { QueryPreview } from '../../../index';

const INITIAL_PANEL_WIDTH_PCT = 52;
const INITIAL_PANEL_HEIGHT_PCT = 50;
const EXPANDED_PANEL_HEIGHT_PCT = 100;
const MIN_PANEL_WIDTH_PX = 240;
const MIN_PANEL_HEIGHT_PX = 112;
const CREATE_AUDIENCE_MODAL_ROW_ID = 'gtCreateAudienceModalRow';

const getResizeHost = (panel: HTMLElement | null) =>
  (panel?.offsetParent as HTMLElement | null) ||
  (typeof document !== 'undefined'
    ? document.getElementById(CREATE_AUDIENCE_MODAL_ROW_ID)
    : null);

const getCollapsedHeightPct = (panel: HTMLElement | null) => {
  const host = getResizeHost(panel);
  if (!host) return INITIAL_PANEL_HEIGHT_PCT;

  return (MIN_PANEL_HEIGHT_PX / host.getBoundingClientRect().height) * 100;
};

const StyledFixedQueryPreview = styled.div<{
  $panelWidthPct: number;
  $panelHeightPct: number;
}>`
  z-index: 30;
  background-color: white;
  border-top: 2px solid ${(props) => props.theme.colors.light_darker_1};
  box-shadow: 0 -4px 16px rgba(25, 25, 39, 0.08);
  width: ${(props) => props.$panelWidthPct}%;
  min-width: ${MIN_PANEL_WIDTH_PX}px;
  height: ${(props) => props.$panelHeightPct}%;
  min-height: ${MIN_PANEL_HEIGHT_PX}px;
`;

const FixedQueryPreview: FC<any> = ({ queryPreview, onClose }) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<boolean>(true);

  const { panelHeightPct, setPanelHeightPct, handleResizePointerDown } =
    usePanelHeightResize(true, previewRef, {
      handleEdge: 'top',
      initialPanelHeightPct: INITIAL_PANEL_HEIGHT_PCT,
    });

  const handleToggleExpanded = () => {
    if (expanded) {
      setPanelHeightPct(getCollapsedHeightPct(previewRef.current));
      setExpanded(false);
      return;
    }

    setPanelHeightPct(EXPANDED_PANEL_HEIGHT_PCT);
    setExpanded(true);
  };

  const handlePanelResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!expanded) setExpanded(true);

      handleResizePointerDown(e);
    },
    [expanded, handleResizePointerDown]
  );

  return (
    <StyledFixedQueryPreview
      ref={previewRef}
      id="gtQueryPreviewContent"
      $panelWidthPct={INITIAL_PANEL_WIDTH_PCT}
      $panelHeightPct={panelHeightPct}
      className="absolute left-0 bottom-0 top-auto flex min-h-0 max-w-full flex-col overflow-hidden bg-white"
    >
      <div
        className={classNames(
          'absolute left-0 top-0 z-20 box-border h-2 w-full cursor-row-resize select-none hover:bg-hx-light-dark',
          'touch-none'
        )}
        data-test-id="query-preview-resize-handle"
        title="Resize preview height"
        onPointerDown={handlePanelResizePointerDown}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize query preview height"
      />
      <div
        className={classNames('flex flex-col', {
          'relative h-full min-h-0 flex-1': expanded,
          'h-auto flex-shrink-0 flex-grow-0': !expanded,
        })}
      >
        <div
          className="flex h-12 flex-shrink-0 items-center rounded-t-lg bg-white px-4"
          style={{
            borderBottom: `1px solid ${theme.colors.light_darker}`,
          }}
        >
          <div className="flex min-w-0 flex-1 items-center">
            <Text tag="p" weight="semi-bold">
              Query preview
            </Text>
          </div>
          <div className="pr-4">
            <Tooltip
              content={expanded ? 'Reduce' : 'Expand'}
              isInfoLabel
              position="top"
            >
              <ButtonIcon
                type="button"
                size="small"
                disabled={false}
                icon={expanded ? 'minus' : 'expand'}
                onClick={handleToggleExpanded}
              />
            </Tooltip>
          </div>
          <Tooltip content="Close" isInfoLabel position="top">
            <ButtonIcon
              type="button"
              size="small"
              disabled={false}
              icon="remove"
              onClick={onClose}
            />
          </Tooltip>
        </div>
        {expanded && (
          <Box className="min-h-0 flex-1 overflow-auto px-4 py-4">
            <QueryPreview
              queryPreview={queryPreview}
              defaultLayout="horizontal"
            />
          </Box>
        )}
      </div>
    </StyledFixedQueryPreview>
  );
};

export default FixedQueryPreview;
